<script setup lang="ts">
import { computed, ref, watch } from "vue"
import type { EventDTO } from "@/js/types/generated"
import type { CalendarView } from "@/js/types/calendar"
import { formatDate } from "@/js/utils/formatDate"
import { getEventMetadataIconComponent } from "@/js/utils/eventMetadataIcons"
import CategoryBadge from "./CategoryBadge.vue"

const props = withDefaults(
	defineProps<{
		events: EventDTO[]
		view: CalendarView
		title: string
		perPage?: number
	}>(),
	{ perPage: 10 },
)

const page = ref(1)
const pageCount = computed(() => Math.max(1, Math.ceil(props.events.length / props.perPage)))
const visibleEvents = computed(() => props.events.slice((page.value - 1) * props.perPage, page.value * props.perPage))

watch(
	() => props.events,
	() => {
		page.value = 1
	},
)
</script>

<template>
	<div class="mt-8">
		<div role="heading" aria-level="2" class="text-2xl font-semibold mb-4">Events for {{ title }}</div>
		<div v-if="events.length === 0" class="text-gray-500 italic">No events this {{ view }}.</div>
		<div role="list" class="flex flex-col gap-4">
			<div
				v-for="event in visibleEvents"
				:key="`${event.id}-${event.start}`"
				role="listitem"
				class="border border-gray-200 rounded-lg overflow-hidden transition-shadow hover:shadow-md"
			>
				<a :href="event.url" class="flex no-underline text-inherit">
					<div v-if="event.thumbnail" class="shrink-0 w-24 sm:w-44">
						<img :src="event.thumbnail" :alt="event.title" class="w-full h-full object-cover" />
					</div>
					<div class="p-4 flex-1 min-w-0">
						<div role="heading" aria-level="3" class="text-lg font-semibold truncate mb-1">
							{{ event.title }}
						</div>
						<div v-if="event.deck" class="text-base font-medium text-gray-700 truncate mb-1">
							{{ event.deck }}
						</div>
						<div class="flex items-center gap-1 text-sm font-medium text-gray-500 mb-2">
							{{ formatDate(event.start, event.end, event.allDay) }}
						</div>
						<CategoryBadge v-if="event.category" :name="event.category" :color="event.color" class="mb-1" />
						<div
							v-for="item in event.metadata"
							:key="item.label + item.value"
							class="flex items-center gap-1 text-sm text-slate-500 mb-1"
						>
							<component
								:is="getEventMetadataIconComponent(item.icon)"
								class="size-4 text-blue-300"
								:aria-label="item.label"
							/>
							{{ item.value }}
						</div>
						<div v-if="event.excerpt" class="text-sm text-gray-700 leading-relaxed line-clamp-2">
							{{ event.excerpt }}
						</div>
					</div>
				</a>
			</div>
		</div>
		<div
			v-if="pageCount > 1"
			class="flex items-center justify-center gap-3 mt-6"
			role="navigation"
			aria-label="Event pages"
		>
			<button
				type="button"
				class="rounded-md border border-[color:var(--sno-cal-button-border,#d1d5db)] bg-[color:var(--sno-cal-button-bg,#f9fafb)] px-3 py-2 text-sm font-medium text-[color:var(--sno-cal-button-text,#374151)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="page <= 1"
				aria-label="Previous page"
				@click="page--"
			>
				←
			</button>
			<div class="text-sm text-gray-600" aria-live="polite">Page {{ page }} of {{ pageCount }}</div>
			<button
				type="button"
				class="rounded-md border border-[color:var(--sno-cal-button-border,#d1d5db)] bg-[color:var(--sno-cal-button-bg,#f9fafb)] px-3 py-2 text-sm font-medium text-[color:var(--sno-cal-button-text,#374151)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
				:disabled="page >= pageCount"
				aria-label="Next page"
				@click="page++"
			>
				→
			</button>
		</div>
	</div>
</template>
